import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../generic-components/Button';
import { useCategorizeTransactionMutation } from '../../../../../hooks/mutations/useCategorizeTransactionMutation';
import { useStore } from '../../../../../store';
import { TransactionCategorizationType } from '../../../enum/transaction-categorization-type';
import { useCategorizeExternalTransaction } from '../../../hooks/useCategorizeExternalTransaction';
import { TransactionTabs } from '../../../state/createTransactionSlice';
import backIcon from '../../../svg/back-arrow.svg';
import { EContentTypes } from '../../TransactionDetailsDrawer';
import { CategoryList } from './CategoryList';
import { CategorySplit } from './CategorySplit';

enum Tabs {
  CHOOSE_CATEGORY,
  SPLIT_CATEGORIES,
}
const CREDIT_CARD_CATEGORY_ID = '68CB366D-3AD8-4D8E-93EB-CFD90AAA8DDE';

export const ChooseCategory = () => {
  const [activeTab, setActiveTab] = React.useState<Tabs>(Tabs.CHOOSE_CATEGORY);
  const { t } = useTranslation();
  const selectedTransaction = useStore((state) => state.selectedTransaction);
  const setSelectedTransaction = useStore((state) => state.setSelectedTransaction);
  const updateTransactionCategory = useStore((state) => state.updateTransactionCategory);
  const setDrawerContentType = useStore((state) => state.setDrawerContentType);
  const selectedTransactionTab = useStore((state) => state.selectedTransactionTab);
  const { accountExternalId } = useStore((state) => state.getPersonalAccount());
  const { mutateAsync: mutateCategorizeTransaction } = useCategorizeTransactionMutation();
  const { mutateAsync: mutateCategorizeExternalTransaction } = useCategorizeExternalTransaction();
  const { customerId } = useStore((state) => state.user);
  const setShowWarningCategoryModal = useStore((state) => state.setShowWarningCategoryModal);

  const handleCategorySelection = async (categoryId: string, businessPercent: number) => {
    updateTransactionCategory(categoryId, businessPercent);
    setSelectedTransaction(null);
    if (selectedTransactionTab === TransactionTabs.LINKED_CREDIT_CARDS) {
      mutateCategorizeExternalTransaction({
        transactionId: selectedTransaction?.transactionId,
        accountExternalId,
        categoryDetails: {
          categoryId,
          businessPercent,
        },
      }).catch(() => {
        console.error('Error categorizing transaction');
      });
    } else {
      if (categoryId === CREDIT_CARD_CATEGORY_ID) {
        setShowWarningCategoryModal(true);
      }
      mutateCategorizeTransaction({
        customerId,
        transactionId: selectedTransaction?.transactionId,
        categoryDetails: {
          categoryId,
          businessPercent,
        },
      })
        .then(() => {
          setSelectedTransaction(null);
        })
        .catch(() => {
          console.error('Error categorizing transaction');
        });
    }
  };

  useEffect(() => {
    if (selectedTransaction?.categorizationType === TransactionCategorizationType.INCOME) {
      setActiveTab(Tabs.CHOOSE_CATEGORY);
    }
  }, [selectedTransaction]);

  return (
    <div className='flex flex-col grow fadeIn'>
      <Button
        onClick={() => setDrawerContentType(EContentTypes.DETAILS)}
        className='ml-10 mt-5'
        id='backFromCategoryPicker'
        childElement={<img src={backIcon} alt='back' className='w-[20px]' />}
      />
      <div className='flex flex-col grow px-[30px]'>
        {/* Tabs */}
        {selectedTransaction?.categorizationType === TransactionCategorizationType.EXPENSE && (
          <div className='flex justify-between mb-[14px]'>
            <button type='button' className='flex-grow' onClick={() => setActiveTab(Tabs.CHOOSE_CATEGORY)}>
              <p
                className={
                  activeTab === Tabs.CHOOSE_CATEGORY
                    ? 'text-center mb-2 font-metricRMedium text-bw08 text-[14px]'
                    : 'text-center mb-2 font-metricR text-bw07 text-[14px]'
                }
              >
                {t('TRANSACTIONS.CHOOSE_CATEGORY')}
              </p>
              <div
                className={activeTab === Tabs.CHOOSE_CATEGORY ? 'border-b-4 border-action03' : 'border-b-2 border-bw03'}
              />
            </button>
            <button type='button' className='flex-grow' onClick={() => setActiveTab(Tabs.SPLIT_CATEGORIES)}>
              <p
                className={
                  activeTab === Tabs.SPLIT_CATEGORIES
                    ? 'text-center mb-2 font-metricRMedium text-bw08 text-[14px]'
                    : 'text-center mb-2 font-metricR text-bw07 text-[14px]'
                }
              >
                {t('TRANSACTIONS.SPLIT_CATEGORIES')}
              </p>
              <div
                className={
                  activeTab === Tabs.SPLIT_CATEGORIES ? 'border-b-4 border-action03' : 'border-b-2 border-bw03'
                }
              />
            </button>
          </div>
        )}

        {activeTab === Tabs.CHOOSE_CATEGORY && (
          <CategoryList transaction={selectedTransaction!} onCategorySelect={handleCategorySelection} />
        )}
        {activeTab === Tabs.SPLIT_CATEGORIES && (
          <CategorySplit transaction={selectedTransaction!} onCategorySelect={handleCategorySelection} />
        )}
      </div>
    </div>
  );
};
